import { toEpochMs } from '../../helpers/time';

/**
 * `GET /human-tasks/me` → view model of the Inicio panel (backend spec §6,
 * decisions D28-D31).
 *
 * Body (v2):
 *   { enabled, receivesTasks, maxVisible, outcomes: [{ id, label, positive }],
 *     visible: Task[], inProgress: Task[],          // every taken + paused, oldest taken first
 *     taken: Task | null, paused: Task | null,      // legacy: the oldest of each
 *     counts: { visible, taken, paused },
 *     today: { done, total } | null }
 *   Task = { id, clientId, clientName, channelId, instruction, context: { summary },
 *            aiCanDo, isMessaging, status, createdAt, expiresAt, visibleAt, takenAt,
 *            pausedAt, label, priority, steps, suggestedReply, estimatedMinutes,
 *            lastInboundAt, lead: { source, fromAds } }
 *
 * A body of the previous backend (only `taken` / `paused`, no card fields,
 * no `today`), or a flat `tasks` list, still reads: every field falls back
 * on its own (null, `normal`, []), and a missing value never drops the task.
 * There is no single "taken" slot: a person can hold any number of tasks.
 */

/** Close outcomes when the company has no list of its own (decisions.md D4). */
export const DEFAULT_OUTCOMES = [
  { id: 'achieved', label: 'Se logró', positive: true },
  { id: 'not_achieved', label: 'No se logró', positive: false },
  { id: 'no_answer', label: 'No respondió', positive: false },
];

const NO_LEAD = Object.freeze({ source: null, fromAds: false });

const text = (value) => (typeof value === 'string' ? value.trim() : '');
const textOrNull = (value) => text(value) || null;

const contextText = (context) => {
  if (typeof context === 'string') return context.trim();
  if (context && typeof context === 'object') return text(context.summary);
  return '';
};

const isCount = (value) => typeof value === 'number' && Number.isInteger(value) && value >= 0;

const stepsOf = (steps) => (Array.isArray(steps) ? steps.map(text).filter(Boolean) : []);

const minutesOf = (value) => (typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.round(value) : null);

const leadOf = (lead) => {
  if (!lead || typeof lead !== 'object') return { ...NO_LEAD };
  return { source: textOrNull(lead.source), fromAds: lead.fromAds === true };
};

export const mapTask = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  const id = text(raw.id) || text(raw._id);
  if (!id) return null;
  const humanOnly = raw.aiCanDo === false;
  const isMessaging = raw.isMessaging === true;
  return {
    id,
    clientId: textOrNull(raw.clientId),
    channelId: textOrNull(raw.channelId),
    clientName: text(raw.clientName) || text(raw.client && raw.client.name),
    instruction: text(raw.instruction),
    context: contextText(raw.context),
    aiCanDo: !humanOnly,
    humanOnly,
    isMessaging,
    status: text(raw.status) || 'visible',
    createdAt: toEpochMs(raw.createdAt),
    // A human-only task never expires (spec §3, rule 3): no countdown for it.
    expiresAt: humanOnly ? null : toEpochMs(raw.expiresAt),
    visibleAt: toEpochMs(raw.visibleAt),
    takenAt: toEpochMs(raw.takenAt),
    pausedAt: toEpochMs(raw.pausedAt),
    // Card details (D29). The backend already sends the label in upper case.
    label: typeof raw.label === 'string' ? textOrNull(raw.label.toUpperCase()) : null,
    priority: raw.priority === 'high' ? 'high' : 'normal',
    steps: stepsOf(raw.steps),
    // Only a messaging task has a reply to suggest (the view filters it too).
    suggestedReply: isMessaging ? textOrNull(raw.suggestedReply) : null,
    estimatedMinutes: minutesOf(raw.estimatedMinutes),
    lastInboundAt: toEpochMs(raw.lastInboundAt),
    lead: leadOf(raw.lead),
  };
};

/** Human-only first (oldest first), then the one that expires soonest (spec §4, "Orden"). */
export const sortVisibleTasks = (tasks) =>
  [...tasks].sort((a, b) => {
    if (a.humanOnly !== b.humanOnly) return a.humanOnly ? -1 : 1;
    if (a.humanOnly) return (a.createdAt ?? Infinity) - (b.createdAt ?? Infinity);
    return (a.expiresAt ?? Infinity) - (b.expiresAt ?? Infinity);
  });

/** Oldest taken first (D28), as the backend sends them; a task without `takenAt` goes last. */
export const sortInProgressTasks = (tasks) =>
  [...tasks].sort((a, b) => (a.takenAt ?? Infinity) - (b.takenAt ?? Infinity));

const mapOutcomes = (outcomes) => {
  if (!Array.isArray(outcomes)) return DEFAULT_OUTCOMES;
  const mapped = outcomes
    .filter((outcome) => outcome && text(outcome.id) && text(outcome.label))
    .map((outcome) => ({ id: text(outcome.id), label: text(outcome.label), positive: outcome.positive === true }));
  return mapped.length > 0 ? mapped : DEFAULT_OUTCOMES;
};

/** "Avance de tu turno" (D31): whole counts with done ≤ total, else null (the panel hides it). */
const mapToday = (today) => {
  if (!today || typeof today !== 'object') return null;
  const { done, total } = today;
  if (!isCount(done) || !isCount(total) || done > total) return null;
  return { done, total };
};

const mapList = (list) => (Array.isArray(list) ? list.map(mapTask).filter(Boolean) : []);

const isInProgress = (task) => task.status === 'taken' || task.status === 'paused';

/** Every task in progress, each once: `inProgress` when sent, else the legacy single slots. */
const partition = (raw) => {
  if (Array.isArray(raw.tasks)) {
    const tasks = mapList(raw.tasks);
    return { inProgress: tasks.filter(isInProgress), visible: tasks.filter((task) => task.status === 'visible') };
  }
  const inProgress = Array.isArray(raw.inProgress)
    ? mapList(raw.inProgress)
    : [mapTask(raw.taken), mapTask(raw.paused)].filter(Boolean);
  return { inProgress, visible: mapList(raw.visible) };
};

export const mapMyTasks = (raw) => {
  const body = raw && typeof raw === 'object' ? raw : {};
  const { inProgress, visible } = partition(body);
  const sortedVisible = sortVisibleTasks(visible);
  const sortedInProgress = sortInProgressTasks(inProgress);
  return {
    enabled: body.enabled === true,
    inProgress: sortedInProgress,
    visible: sortedVisible,
    today: mapToday(body.today),
    maxVisible: isCount(body.maxVisible) ? body.maxVisible : null,
    outcomes: mapOutcomes(body.outcomes),
    // Every open task of the user: the nav badge.
    pendingCount: sortedVisible.length + sortedInProgress.length,
  };
};

/** The conversations of the tasks in progress (the chat's focus list). */
export const inProgressConversationIds = (me) => (me && Array.isArray(me.inProgress)
  ? me.inProgress.map((task) => task.clientId).filter(Boolean)
  : []);

export default mapMyTasks;
